import { apiGet } from "./apiClient";

/**
 * Data layer + geometry helpers for the session detail path/coverage view.
 *
 * These talk to the same field-support backend the rest of the app uses:
 *   - GET /telemetry/trial/{trial}?device_id=&limit=   → per-second robot state
 *   - GET /sessions/{trial}/perimeter?device_id=       → field boundary
 *   - GET /sessions/{trial}/coverage?device_id=&…      → server-rendered PNG + stats
 *
 * The path is derived from telemetry rows (lat/lng per timestamp); "mowing"
 * vs "transit" is decided by the blade state, mirroring the field-support app:
 *   bladesActive = blade_toggle_status ?? (blade_rpm > 0)
 */

export interface GpsPoint {
  latitude: number;
  longitude: number;
  h_acc: number;
  /** Unix ms. */
  timestamp: number;
  bladesActive: boolean;
}

export interface PerimeterPoint {
  latitude: number;
  longitude: number;
}

export interface CoverageStats {
  totalArea: number; // m²
  coveredArea: number; // m²
  missedArea: number; // m²
  coveragePercent: number;
  gapCount: number;
  largestGapArea: number; // m²
  overlapArea: number; // m²
  overlapPercent: number;
  excessOverlapArea: number; // m²
  excessOverlapPercent: number;
  efficiencyScore: number; // 0–100
}

export interface CoverageResponse {
  stats: CoverageStats;
  coverageMapBase64: string | null;
  gpsPointCount: number;
}

interface RawTelemetry {
  latitude?: number | null;
  longitude?: number | null;
  gps_h_accuracy?: number | null;
  timestamp: string;
  blade_toggle_status?: boolean | null;
  blade_rpm?: number | null;
}
interface TelemetryListResponse {
  count: number;
  telemetry: RawTelemetry[];
}

interface RawPerimeterPoint {
  latitude: number;
  longitude: number;
  point_sequence?: number | null;
  id?: number | null;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  }
  const str = s.toString();
  return str ? `?${str}` : "";
}

/** (0,0) is the uninitialised-GPS sentinel; treat it as no fix. */
export function hasValidCoord(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0)
  );
}

/** Robot GPS path for a session, sorted by time, invalid fixes dropped. */
export async function fetchTrialPath(
  trialNumber: string,
  deviceId: string,
  limit = 10000,
): Promise<GpsPoint[]> {
  const res = await apiGet<TelemetryListResponse>(
    `/telemetry/trial/${encodeURIComponent(trialNumber)}${qs({
      device_id: deviceId,
      limit,
    })}`,
  );
  return res.telemetry
    .filter(
      (t) =>
        typeof t.latitude === "number" &&
        typeof t.longitude === "number" &&
        hasValidCoord(t.latitude as number, t.longitude as number),
    )
    .map((t) => ({
      latitude: t.latitude as number,
      longitude: t.longitude as number,
      h_acc: t.gps_h_accuracy ?? 0.5,
      timestamp: new Date(t.timestamp).getTime(),
      bladesActive: t.blade_toggle_status ?? (t.blade_rpm ?? 0) > 0,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

/** Field boundary polygon (auto-closed). Returns [] when none is recorded. */
export async function fetchSessionPerimeter(
  trialNumber: string,
  deviceId: string,
): Promise<PerimeterPoint[]> {
  const raw = await apiGet<RawPerimeterPoint[]>(
    `/sessions/${encodeURIComponent(trialNumber)}/perimeter${qs({
      device_id: deviceId,
    })}`,
  );
  const pts = raw
    .filter((p) => typeof p.latitude === "number" && typeof p.longitude === "number")
    .sort((a, b) => (a.point_sequence ?? a.id ?? 0) - (b.point_sequence ?? b.id ?? 0))
    .map((p) => ({ latitude: p.latitude, longitude: p.longitude }));
  // Close the loop if it isn't already.
  if (pts.length > 2) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (Math.abs(first.latitude - last.latitude) + Math.abs(first.longitude - last.longitude) > 1e-6) {
      pts.push({ ...first });
    }
  }
  return pts;
}

/** Server-computed coverage stats + base64 PNG overlay. */
export async function fetchSessionCoverage(
  trialNumber: string,
  deviceId: string,
  maxTimestamp?: number,
  includeMap = true,
): Promise<CoverageResponse> {
  const data = await apiGet<{
    stats: Record<string, number>;
    coverage_map_base64: string | null;
    gps_point_count: number;
  }>(
    `/sessions/${encodeURIComponent(trialNumber)}/coverage${qs({
      device_id: deviceId,
      max_timestamp: maxTimestamp,
      include_map: includeMap,
    })}`,
  );
  const s = data.stats;
  return {
    stats: {
      totalArea: s.total_area,
      coveredArea: s.covered_area,
      missedArea: s.missed_area,
      coveragePercent: s.coverage_percent,
      gapCount: s.gap_count,
      largestGapArea: s.largest_gap_area,
      overlapArea: s.overlap_area,
      overlapPercent: s.overlap_percent,
      excessOverlapArea: s.excess_overlap_area,
      excessOverlapPercent: s.excess_overlap_percent,
      efficiencyScore: s.efficiency_score,
    },
    coverageMapBase64: data.coverage_map_base64,
    gpsPointCount: data.gps_point_count,
  };
}

// ---- Geometry helpers ------------------------------------------------------

/** 21-inch cutting deck, in metres — matches the field-support app. */
export const MOWER_WIDTH_METERS = 21 * 0.0254;
const SQM_PER_ACRE = 4046.8564224;

export function sqmToAcres(sqm: number): number {
  return sqm / SQM_PER_ACRE;
}

export interface PathSegment {
  positions: [number, number][];
  mowing: boolean;
}

/**
 * Split a GPS path into consecutive runs of blades-on ("mowing") and
 * blades-off ("transit") so each can be drawn in its own style.
 */
export function segmentPath(points: GpsPoint[]): PathSegment[] {
  if (points.length < 2) return [];
  const segments: PathSegment[] = [];
  let mowing = points[0].bladesActive;
  let current: [number, number][] = [[points[0].latitude, points[0].longitude]];
  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    const isMowing = p.bladesActive;
    current.push([p.latitude, p.longitude]);
    if (isMowing !== mowing) {
      segments.push({ positions: current, mowing });
      // New segment starts at the boundary point so lines stay connected.
      current = [[p.latitude, p.longitude]];
      mowing = isMowing;
    }
  }
  if (current.length > 1) segments.push({ positions: current, mowing });
  return segments;
}

/** Heading in degrees (0 = north, clockwise) from the last two points. */
export function computeHeadingDeg(points: GpsPoint[]): number {
  if (points.length < 2) return 0;
  const a = points[points.length - 2];
  const b = points[points.length - 1];
  const dLat = b.latitude - a.latitude;
  const dLng = b.longitude - a.longitude;
  if (dLat === 0 && dLng === 0) return 0;
  return (Math.atan2(dLng, dLat) * 180) / Math.PI;
}

/**
 * Index of the last point at or before `maxTs` (binary search; points are
 * time-sorted). Returns -1 if every point is after `maxTs`.
 */
export function pathCutoffIndex(points: GpsPoint[], maxTs: number): number {
  let lo = 0;
  let hi = points.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid].timestamp <= maxTs) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/** [[south, west], [north, east]] bounds over all supplied points, or null. */
export function boundsOf(
  ...groups: { latitude: number; longitude: number }[][]
): [[number, number], [number, number]] | null {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const g of groups) {
    for (const p of g) {
      if (!hasValidCoord(p.latitude, p.longitude)) continue;
      minLat = Math.min(minLat, p.latitude);
      maxLat = Math.max(maxLat, p.latitude);
      minLng = Math.min(minLng, p.longitude);
      maxLng = Math.max(maxLng, p.longitude);
    }
  }
  if (!Number.isFinite(minLat)) return null;
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}
